<template>
  <!-- Modal for collecting a user message (reason for update/delete) -->
  <BModal
      v-model="isOpen"
      centered
      :title="title"
      :ok-title="okText"
      :cancel-title="cancelText"
      :ok-disabled="required && !isValid"
      @ok.prevent="onOk"
      @cancel="onCancel"
      @hide="onHide"
  >
    <!-- Optional description above the textarea -->
    <p v-if="description" class="mb-2">{{ description }}</p>

    <!-- Textarea for user input -->
    <BFormTextarea
        v-model="message"
        :placeholder="placeholder"
        rows="4"
        :state="messageState"
        @keydown.enter.exact.prevent
    />

    <!-- Validation message for minimum length -->
    <div v-if="required" class="small mt-2" :class="{ 'text-danger': !isValid, 'text-muted': isValid }">
      Minimum {{ minLength }} tegn.
    </div>

    <!-- Custom footer with Cancel and Ok buttons -->
    <template #footer>
      <div class="w-100 d-flex justify-content-end gap-2">
        <BButton variant="outline-secondary" @click="onCancel">{{ cancelText }}</BButton>
        <BButton variant="primary" :disabled="required && !isValid" @click="onOk">{{ okText }}</BButton>
      </div>
    </template>
  </BModal>
</template>

<script setup>
// Modal for collecting a reason from the user. Exposes an async open() method that returns the message or null.
import { useEmployeeStore } from 'stores/employeeStore'
import { computed, ref } from 'vue'
import { BModal, BButton, BFormTextarea } from 'bootstrap-vue-next'

// Store
const employeeStore = useEmployeeStore()

// Modal open/close state
const isOpen = ref(false)
// User's message input
const message = ref('')

// Modal content and options
const title = ref('Begrundelse')
const description = ref('')
const action = ref('')
const placeholder = ref('Skriv en kort begrundelse…')
const okText = ref('Gem')
const cancelText = ref('Annuller')
const required = ref(true)
const minLength = ref(10)
const record = ref(null)

// Resolver for the open() Promise
let resolver = null

// Normalized message (trimmed, single spaces)
const normalized = computed(() => message.value.toString().trim().replace(/\s+/g, ' '))
// Valid if not required, or meets minLength
const isValid = computed(() => !required.value || (normalized.value.length >= minLength.value))

// Used for input validation state (null = untouched, true/false = valid/invalid)
const messageState = computed(() => {
  if (!required.value) return null
  if (!isOpen.value) return null
  if (message.value.length === 0) return null
  return isValid.value
})

async function readMesage() {
  if (record.value) {
    await employeeStore.GET_UPDATE_MESSAGE_POLICIES()

    const action_value = action.value === 'destroy' ? 'destroy_action' : 'update_action'
    const controller = record.value.controller

    // Find the current policy
    const policy = employeeStore.update_message_policies.find(p => p.policy_action === action_value && p.record_type === controller.object_name)
    if (policy && policy.description) {
        return policy.description || ''
    }

    if (action.value === 'destroy') {
        return 'Skriv venligst en begrundelse for sletning.'
    } else {
        return 'Skriv venligst en begrundelse for ændringen.'
    }
  }
  return ''
}

// Opens the modal with options, returns a Promise resolved with the message or null
async function open(options = {}) {
  title.value = options.title ?? 'Begrundelse'
  action.value = options.action ?? null
  placeholder.value = options.placeholder ?? 'Skriv en kort begrundelse…'
  okText.value = options.okText ?? 'Ok'
  cancelText.value = options.cancelText ?? 'Annuller'
  record.value = options.record ?? null
  required.value = options.required ?? true
  minLength.value = options.minLength ?? 10

  description.value = await readMesage()

  // message.value = options.initialMessage ?? ''
  isOpen.value = true

  return new Promise((resolve) => {
    resolver = resolve
  })
}

// Helper to resolve the open() Promise and close the modal
function resolveAndClose(val) {
  message.value = ''
  const r = resolver
  resolver = null
  isOpen.value = false
  if (r) r(val)
}

// Called when Ok is pressed
function onOk() {
  if (required.value && !isValid.value) return
  resolveAndClose(normalized.value)
}

// Called when Cancel is pressed
function onCancel() {
  resolveAndClose(null)
}

// Called when modal is hidden (ESC or backdrop)
function onHide() {
  // If user closes modal via ESC or backdrop
  if (resolver) resolveAndClose(null)
}

// Expose open() so parent can call it
defineExpose({ open })
</script>
